// Fake data for the "Purchase list with add-in-a-sheet" wireframe. Shapes
// follow the live Purchase page: a product has one line per market.

export type ListStatus = "draft" | "in_progress" | "arriving"
export type MarketCode = "VN" | "INT"
export type ChartShape = "falling" | "jump" | "flat" | "out" | "none"
export type Tab = "buy" | "no_plan" | "no_supplier"

export interface Line {
  market: MarketCode
  safety: number | null
  need: number | null
  have: number
  arriving: number | null
  short: number | null
  quantity: number
  chart: ChartShape
}

export interface ListProduct {
  id: string
  name: string
  supplier: string | null
  moq: number | null
  leadTimeDays: number
  status: ListStatus
  lines: Line[]
  // Which tab of the add sheet it shows under, while not on the list.
  tab: Tab
  justAdded?: boolean
}

export const MARKETS: Record<MarketCode, { name: string; currency: string }> = {
  VN: { name: "Vietnam", currency: "VND" },
  INT: { name: "International", currency: "USD" },
}

export const SUPPLIERS = ["Bestop", "Infly", "Seago", "Lena", "Thien Phuc", "Shizencos"]

const line = (market: MarketCode, numbers: Partial<Line>): Line => ({
  market, safety: null, need: null, have: 0, arriving: null, short: null, quantity: 0, chart: "none", ...numbers,
})

// On the list today: three drafts, then in progress, then arriving.
export const ON_LIST: ListProduct[] = [
  { id: "13856", name: "Bàn Chải Điện Halio SonicLite Electric Toothbrush - Ocean Blue", supplier: "Infly", moq: 3000, leadTimeDays: 60, status: "draft", tab: "buy",
    lines: [line("VN", { safety: 3061, need: 2951, have: 1214, arriving: null, short: 4798, quantity: 4868, chart: "out" })] },
  { id: "13606", name: "Set 5 Đầu Bàn Chải Thay Thế Halio Sonic Whitening Replacement Brush Heads Rose Gold", supplier: "Seago", moq: 3000, leadTimeDays: 45, status: "draft", tab: "buy",
    lines: [line("VN", { safety: 1803, need: 2497, have: 1545, arriving: 3000, short: 0, quantity: 3927, chart: "jump" })] },
  { id: "13831", name: "Máy Tạo Kiểu Halio 2in1 Airplus Hair Styler", supplier: "Lena", moq: 1000, leadTimeDays: 60, status: "draft", tab: "buy",
    lines: [line("VN", { safety: 1369, need: 3070, have: 1010, arriving: 4016, short: 0, quantity: 3462, chart: "jump" })] },
  { id: "11136", name: "Máy tăm nước cầm tay 6 chế độ Halio Professional Cordless Oral Irrigator", supplier: "Bestop", moq: 1000, leadTimeDays: 60, status: "in_progress", tab: "buy",
    lines: [line("VN", { safety: 2775, need: 4163, have: 614, arriving: 7000, short: 0, quantity: 6963, chart: "jump" })] },
  { id: "14277", name: "Mặt Nạ Gel Phục Hồi Halio L-PEP LED Red Light Enhancing Hydration Gel Mask", supplier: "Lena", moq: null, leadTimeDays: 60, status: "in_progress", tab: "buy",
    lines: [line("INT", { have: 0, arriving: 20000, quantity: 20000 })] },
  { id: "14267", name: "Dao Cạo Nữ 5 Lưỡi Lixibox Silk Touch Razor", supplier: "Thien Phuc", moq: 3000, leadTimeDays: 45, status: "in_progress", tab: "buy",
    lines: [line("VN", { have: 0, arriving: 3000, quantity: 3000 })] },
  { id: "13663", name: "Máy Rửa Mặt Massage Halio Multi-task Cleansing & Massaging Device 5 in 1 - Sage Green", supplier: "Infly", moq: 1000, leadTimeDays: 60, status: "arriving", tab: "buy",
    lines: [line("VN", { safety: 288, need: 962, have: 28315, arriving: 1000, short: 0, chart: "flat" })] },
  { id: "14196", name: "Thiết bị Ánh Sáng Sinh Học Halio RegenBoost Red Light Panel", supplier: "Lena", moq: null, leadTimeDays: 60, status: "arriving", tab: "buy",
    lines: [line("VN", { have: 0, arriving: 100 }), line("INT", { safety: 0, need: 750, have: 872, arriving: 1650, short: 0, chart: "jump" })] },
  { id: "13963", name: "Máy Triệt Lông Lạnh Sapphire Halio InfinityGlow IPL Hair Removal Device Kèm Set Gift - Soft Pink", supplier: "Infly", moq: 1000, leadTimeDays: 60, status: "arriving", tab: "buy",
    lines: [line("VN", { safety: 2944, need: 8420, have: 59906, arriving: 6200, short: 0, chart: "flat" })] },
  { id: "13201", name: "Bàn Chải Điện Halio Sonic Whitening - Pearl White", supplier: "Infly", moq: 3000, leadTimeDays: 60, status: "arriving", tab: "buy",
    lines: [line("VN", { safety: 1200, need: 4300, have: 7800, arriving: 3000, short: 0, chart: "falling" })] },
]

// Not on the list: what the add sheet offers, by tab.
export const CANDIDATES: ListProduct[] = [
  { id: "13410", name: "Máy Rửa Mặt Halio Facial Cleansing & Massaging Device - Lavender", supplier: "Infly", moq: 1000, leadTimeDays: 60, status: "draft", tab: "buy",
    lines: [line("VN", { safety: 980, need: 2600, have: 700, arriving: 500, short: 1380, quantity: 1380, chart: "out" }), line("INT", { safety: 120, need: 500, have: 120, arriving: null, short: 500, quantity: 500, chart: "out" })] },
  { id: "13987", name: "Son Tint Lustre Rose Lip Tint", supplier: "Thien Phuc", moq: 3000, leadTimeDays: 45, status: "draft", tab: "buy",
    lines: [line("VN", { safety: 600, need: 3200, have: 1900, arriving: null, short: 1900, quantity: 3000, chart: "out" })] },
  { id: "14012", name: "Bông Tẩy Trang Okame Cotton Pads 120pcs", supplier: "Shizencos", moq: 5000, leadTimeDays: 50, status: "draft", tab: "buy",
    lines: [line("VN", { safety: 1500, need: 7400, have: 2100, arriving: 1000, short: 5800, quantity: 5800, chart: "out" })] },
  { id: "13555", name: "Bàn Chải Điện Trẻ Em Seago Kids Toothbrush", supplier: "Seago", moq: 1000, leadTimeDays: 40, status: "draft", tab: "buy",
    lines: [line("VN", { safety: 200, need: 1050, have: 310, arriving: null, short: 940, quantity: 1000, chart: "out" })] },
  { id: "14301", name: "Đầu Bàn Chải Thay Thế Halio Replacement Brush Heads", supplier: "Infly", moq: 2000, leadTimeDays: 60, status: "draft", tab: "no_plan",
    lines: [line("VN", { have: 340, arriving: null, quantity: 2000, chart: "falling" })] },
  { id: "14120", name: "Má Hồng Lustre Velvet Blush", supplier: "Thien Phuc", moq: 3000, leadTimeDays: 45, status: "draft", tab: "no_plan",
    lines: [line("VN", { have: 90, arriving: null, quantity: 3000, chart: "falling" })] },
  { id: "14455", name: "Sữa Rửa Mặt Shizen Green Tea Cleanser", supplier: null, moq: null, leadTimeDays: 0, status: "draft", tab: "no_supplier",
    lines: [line("VN", { have: 600 })] },
  { id: "14460", name: "Cọ Mặt Nạ Silicone Ningbo Face Mask Brush", supplier: null, moq: null, leadTimeDays: 0, status: "draft", tab: "no_supplier",
    lines: [line("VN", { have: 8800 })] },
]

export const units = (value: number) => value.toLocaleString("en-US")
